import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useThemeStore } from '../stores/themeStore';
import { useAuthStore } from '../stores/authStore';

export interface DrawerMenuItem {
  id: string;
  label: string;
  icon: string;
  onPress: () => void;
  destructive?: boolean;
}

interface UseDrawerMenuItemsOptions {
  onCloseDrawer: () => void;
}

export function useDrawerMenuItems({ onCloseDrawer }: UseDrawerMenuItemsOptions): DrawerMenuItem[] {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useThemeStore();
  const signOut = useAuthStore((s) => s.signOut);

  return useMemo(
    (): DrawerMenuItem[] => [
      {
        id: 'theme',
        label: theme === 'dark' ? t('drawer.theme.light') : t('drawer.theme.dark'),
        icon: theme === 'dark' ? 'sun' : 'moon',
        onPress: () => toggleTheme(),
      },
      {
        id: 'faq',
        label: t('drawer.faq'),
        icon: 'help-circle',
        onPress: () => {
          onCloseDrawer();
          navigate('/faq');
        },
      },
      {
        id: 'about',
        label: t('drawer.about'),
        icon: 'info',
        onPress: () => {
          onCloseDrawer();
          navigate('/about');
        },
      },
      {
        id: 'privacy',
        label: t('drawer.privacy'),
        icon: 'shield',
        onPress: () => {
          onCloseDrawer();
          navigate('/privacy');
        },
      },
      {
        id: 'settings',
        label: t('drawer.settings', 'Settings'),
        icon: 'settings',
        onPress: () => {
          onCloseDrawer();
          navigate('/settings');
        },
      },
      {
        id: 'signout',
        label: t('drawer.signOut'),
        icon: 'log-out',
        onPress: () => {
          onCloseDrawer();
          if (window.confirm(t('drawer.signOutMessage', 'Are you sure you want to sign out?'))) {
            signOut().then(() => navigate('/'));
          }
        },
        destructive: true,
      },
    ],
    [theme, toggleTheme, t, navigate, onCloseDrawer, signOut]
  );
}
